import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { SelectionContext } from "./SelectionContext";
import Row from "./Row";
import { useScreenSize } from "../index";
import "../../styles/list.scss";

import SortIcon from "../../assets/dataTable/sort-icon.svg";
import Skeleton from "react-loading-skeleton";
import { ReactComponent as EmptySvg } from "../../assets/icons/empty-projects.svg";

import { useTranslation } from "react-i18next";

export const STATE = {
  LOADING: "LOADING",
  ERROR: "ERROR",
  IDLE: "IDLE",
  VALIDATING: "VALIDATING",
};

const SortIconButton = ({ icon, sortNumber }) => {
  if (!sortNumber) return null;
  if (sortNumber !== 1 && sortNumber !== -1) return null;

  return (
    <img
      src={icon || SortIcon}
      width="12px"
      height="10px"
      className="c-table-header__icon--sort"
      style={{
        rotate: sortNumber === 1 ? "180deg" : "0deg",
      }}
    />
  );
};

/**
 *
 * @description https://www.notion.so/pttnewpos/List-a3de6c74a6494d659ca03fbe3678ea8d
 * @param {Array} columns - Array of objects that contains the header configurations
 * @param {boolean} scrollable - If true, the table will be scrollable
 * @param {Array} dataSource - Array of objects that contains the data to be displayed
 * @param {boolean} viewable - If true, the table will be clickable
 * @param {boolean} enableSelection - If true, the table will have checkbox on the left of each row
 * @param {boolean} enableSelectionHeader - If true, the table will have checkbox on the left of the header
 * @param {boolean} hasFooter - If true, the table will have a footer
 * @param {string} moreIconTooltip - Tooltip for the more icon
 * @param {number} numberOfLines - Number of lines to be displayed on each row
 * @param {string} verticalAlign - Vertical alignment of the content
 * @param {boolean} showBlock - If true, table will open border for show block of data
 * @param {boolean} monospaceNumbers - If true, the numbers will be monospaced font
 * @param {boolean} disableBorderRadius - If true, the table will have no border radius
 * @param {function} onRecordClick - Callback function when a row is clicked
 * @param {function} onAddSelection - Callback function when a row is selected
 * @param {function} onRemoveSelection - Callback function when a row is unselected
 * @param {function} onSort - Callback function when a header is clicked
 */

const List = (props) => {
  const selectionsContext = useContext(SelectionContext);
  const [headers, setHeaders] = useState([]);
  const [records, setRecords] = useState([]);
  const [recordsView, setRecordsView] = useState([]);

  const [selected, setSelected] = useState(false);
  const [countSelection, setCountSelection] = useState(0);
  const [selectionHeader, setSelectionHeader] = useState(false);

  const [sorted, setCurrentSorted] = useState({});
  const [indexOfGroupHeader, setIndexOfGroupHeader] = useState([]);
  const [indexOfNestedGroupHeader, setIndexOfNestedGroupHeader] = useState({});
  const [secondPagination, setSecondPagination] = useState(false);
  const [sizeWatch, setSizeWatch] = useState([]);

  const { screenSize, screenWidth } = useScreenSize();

  const monospaceNumbers = props.monospaceNumbers || false;
  const { t } = useTranslation();

  const getHeaders = (dataSource, columns) => {
    let headers = [];
    let headerIndexes = {};

    if (columns) {
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i];

        if (column.screenSize === "l" && !["xl", "lg"].includes(screenSize))
          continue;

        if (column.screenSize === "m" && !["md", "sm"].includes(screenSize))
          continue;

        if (column.screenSize === "s" && !["xs"].includes(screenSize)) continue;

        if (column.screenWidth && screenWidth < column.screenWidth) continue;

        headers.push(column);
        headerIndexes[column.key] = i;
      }

      return {
        headers,
        headerIndexes,
      };
    }

    for (const key in dataSource[0]) {
      if (!Object.hasOwnProperty.call(dataSource[0], key)) continue;
      const newLength = headers.push({ display: key, key });
      headerIndexes[key] = newLength - 1;
    }

    return {
      headers,
      headerIndexes,
    };
  };

  const processData = () => {
    const { dataSource, columns } = props;

    const records = [];
    const recordsviews = [];
    // * Get headers
    const { headers, headerIndexes } = getHeaders(dataSource, columns);
    let indexRecord = 0;
    // * Group and separate headers and records
    for (const dataRecord of dataSource || []) {
      let record = new Array(headers.length);
      let checkColumnData = true;
      for (const column of headers) {
        let key = column.key;
        if (key in dataRecord || column.columns) continue;
        checkColumnData = false;
      }
      if (checkColumnData === false) {
        for (let i = 0; i < record.length; i++) {
          record[i] = "";
        }
      }
      for (const key in dataRecord) {
        if (!Object.hasOwnProperty.call(dataRecord, key)) continue;

        const headerIndex = headerIndexes[key];

        // * Skip if no header match
        if (headerIndex === undefined) continue;

        record[+headerIndex] = dataRecord[key];
      }

      if (dataRecord.viewable != undefined) {
        if (!dataRecord.viewable) {
          recordsviews.push(indexRecord);
        }
      }
      records.push(record);
      indexRecord++;
    }

    const indexOfGroupHeaders = [];
    for (const header of headers) {
      if (header.columns) {
        indexOfGroupHeaders.push(header.key);
      }
    }

    const indexOfNestedGroupHeaders = {};
    for (const header of headers) {
      if (header.columns) {
        for (let count = 0; count < header.columns.length; count++) {
          indexOfNestedGroupHeaders[header.columns[count].key] = count + 1;
        }
      }
    }

    setIndexOfNestedGroupHeader(indexOfNestedGroupHeaders);
    setIndexOfGroupHeader(indexOfGroupHeaders);
    setRecordsView(recordsviews);
    setRecords(records);
    setHeaders(headers);
  };

  const handleRecordClick = (e) => {
    if (props.onRecordClick) props.onRecordClick(e);
  };

  useEffect(() => {
    if (props.children) {
      if (
        props.children.type?.name === "SecondaryPagination" ||
        props.children.props?.name === "secondaryPagination"
      ) {
        setSecondPagination(true);
      } else {
        setSecondPagination(true);
      }
    }

    processData();
  }, []);

  useEffect(() => {
    processData();
  }, [props.dataSource]);

  useEffect(() => {
    setSelected(selectionHeader);
  }, [selectionHeader]);

  useEffect(() => {
    const { dataSource } = props;
    if (countSelection === 0) {
      setSelectionHeader(false);
    } else if (countSelection === dataSource.length) {
      setSelectionHeader(true);
    }
  }, [countSelection]);

  useEffect(() => {
    let initialSortItem = {};

    for (let header of props.columns) {
      if (header.sortable) {
        initialSortItem[`${header.key}`] = null;
      }
    }

    setCurrentSorted(initialSortItem);
  }, [props.columns]);

  useEffect(() => {
    if (props.currentSort) {
      setCurrentSorted((prev) => ({ ...prev, ...props.currentSort }));
    }
  }, [props.currentSort]);

  const addSelection = (e) => {
    setCountSelection(countSelection + 1);
    if (props.onAddSelection) props.onAddSelection(e);
  };

  const removeSelection = (e) => {
    setCountSelection(countSelection - 1);
    if (props.onRemoveSelection) props.onRemoveSelection(e);
  };

  const handleSelectionChanged = (e) => {
    const { dataSource } = props;
    if (e === true) {
      setCountSelection(dataSource.length);
      if (props.onAddSelection) props.onAddSelection(dataSource);
    } else if (e === false) {
      setCountSelection(0);
      if (props.onRemoveSelection) props.onRemoveSelection(dataSource);
    }
    setSelectionHeader(e);
  };

  const handleOnSortedBy = (id) => {
    setCurrentSorted((prev) => {
      let prevKey = Object.keys(prev);

      if (!prevKey.includes(id)) {
        props.onSort({ ...prev, [id]: -1 });
        return { ...prev, [id]: -1 };
      }

      let currentSortedBy = prev[`${id}`];
      let changeSorted = null;

      switch (currentSortedBy) {
        case null:
          changeSorted = -1;
          break;
        case -1:
          changeSorted = 1;
          break;
      }

      props.onSort({ ...prev, [id]: changeSorted });

      return { ...prev, [id]: changeSorted };
    });
  };

  const getContentRender = () => {
    const {
      enableSelection,
      dataSource,
      contentState,
      viewable,
      moreIconTooltip,
      numberOfLines,
      verticalAlign,
    } = props;

    function emptyRow(text, color) {
      let countGroupColumns = 0;
      for (let h of headers) {
        if (h.columns) {
          countGroupColumns += h.columns.length;
        }
      }
      return (
        <tr className="c-table-records__record c-table-records__record--empty">
          <td
            colSpan={headers.length + countGroupColumns + 2}
            style={{
              color: color || "inherit",
              borderBottomLeftRadius: secondPagination ? "0px" : "10px",
              borderBottomRightRadius: secondPagination ? "0px" : "10px",
            }}
          >
            <div>
              <EmptySvg height={224} />
              <span>No results found</span>
            </div>
          </td>
        </tr>
      );
    }

    if (contentState === STATE.ERROR) {
      return emptyRow(t("list.error"), "red");
    }

    if (contentState === STATE.LOADING) {
      const skeletonData = [0, 0, 0, 0];
      return skeletonData.map((_, i) => (
        <tr
          className="c-table-records__record c-table-records__record--loading"
          key={i}
        >
          <td />
          {headers.map((_header, j) => {
            /**
             * Map skeleton group header
             */
            if (Array.isArray(_header.columns)) {
              return _header.columns.map((_, k) => (
                <td key={k}>
                  <Skeleton className="c-table-records__skeleton" height={20} />
                </td>
              ));
            }

            let styleRadius = { borderBottomRightRadius: "0px" };

            if (i === skeletonData.length - 1 && j === headers.length - 1) {
              styleRadius = { borderBottomRightRadius: "10px" };
            }

            return (
              <td key={j} style={styleRadius}>
                <Skeleton className="c-table-records__skeleton" height={20} />
              </td>
            );
          })}
          <td />
        </tr>
      ));
    }

    if (records.length === 0) {
      // * Empty
      return emptyRow(t("list.noResult"));
    }

    const { selections } = selectionsContext;

    let maximumRow = 1;

    return records.map((record, i) => {
      const header = [];
      const headerAlign = [];
      const columnColor = [];
      const nestedHeaderAlign = [];

      const floatingPoints = {};
      const nestedFloatingPoints = {};
      const textColors = {};

      let rowSpan = 1;
      const rowId = `row-${i}`;

      for (let findRow of record) {
        if (Array.isArray(findRow)) {
          rowSpan = findRow.length;
        }
      }

      if (rowSpan > maximumRow) {
        maximumRow = rowSpan;
      }

      headers.forEach((h, i) => {
        if (h.maxWidth) {
          header.push(h.maxWidth + "px");
        } else {
          header.push(null);
        }

        if (h.textAlign) {
          headerAlign.push(h.textAlign);
        } else {
          headerAlign.push(null);
        }

        if (h.columnColor) {
          columnColor.push(h.columnColor);
        } else {
          columnColor.push(null);
        }

        // * check floating point is not empty and create hash with index of header
        if (h.floatingPoint) {
          floatingPoints[i] = h.floatingPoint;
        }

        // * check text color and change text in column
        if (h.textColor) {
          textColors[i] = h.textColor;
        }

        if (h.columns) {
          const textAlignStores = [];

          for (let i = 0; i < h.columns.length; i++) {
            if (h.columns[i].textAlign) {
              textAlignStores.push(h.columns[i].textAlign);
            }

            if (h.columns[i].floatingPoint) {
              nestedFloatingPoints[h.columns[i].key] =
                h.columns[i].floatingPoint;
            }
          }

          nestedHeaderAlign.push(textAlignStores);
        }
      });

      if (props.hasFooter && i === records.length - 1) {
        for (const checkItem of record) {
          if (Array.isArray(checkItem)) {
            return;
          }
        }

        return (
          <Row
            id={rowId}
            key={rowId}
            header={header}
            headerAlign={headerAlign}
            recordsView={recordsView}
            floatingPoints={floatingPoints}
            nestedFloatingPoints={nestedFloatingPoints}
            records={record}
            viewable={viewable}
            hasFooter={props.hasFooter}
            enableSelection={enableSelection}
            moreIconTooltip={moreIconTooltip}
            numberOfLines={numberOfLines}
            verticalAlign={verticalAlign}
            monospaceNumbers={monospaceNumbers}
            secondPagination={secondPagination}
          />
        );
      }

      return (
        <Row
          id={rowId}
          key={rowId}
          header={header}
          textColors={textColors}
          headerAlign={headerAlign}
          showBlock={props.showBlock}
          nestedHeaderAlign={nestedHeaderAlign}
          floatingPoints={floatingPoints}
          nestedFloatingPoints={nestedFloatingPoints}
          indexOfGroupHeader={indexOfGroupHeader}
          indexOfNestedGroupHeader={indexOfNestedGroupHeader}
          finalRow={i === records.length - 1}
          columnColor={columnColor}
          recordsView={recordsView}
          records={record}
          allRecord={records.length}
          maximumRow={maximumRow}
          rowSpan={rowSpan}
          selectedAllRecords={selected}
          viewable={viewable}
          enableSelection={enableSelection}
          monospaceNumbers={monospaceNumbers}
          selected={selections.includes(dataSource[i])}
          onRecordClick={() => handleRecordClick(dataSource[i])}
          onSelectionChanged={(add) => {
            if (add) {
              addSelection(dataSource[i]);
              selectionsContext.addSelection(dataSource[i]);
            } else {
              removeSelection(dataSource[i]);
              selectionsContext.removeSelection(dataSource[i]);
            }
          }}
          moreIconTooltip={moreIconTooltip}
          numberOfLines={numberOfLines}
          verticalAlign={verticalAlign}
          secondPagination={secondPagination}
          disableBorderRadius={props.disableBorderRadius}
        />
      );
    });
  };

  const classHeader = ` c-table__header-${props.className}`;
  const classLabel = ` c-table-header__label-${props.className}`;
  let classNameThead = `c-table__header${props.className ? classHeader : ""}`;
  let classNameLabel = `c-table-header__label${
    props.className ? classLabel : ""
  }`;

  const browserStyle = {
    height: "50px",
  };

  if (navigator.userAgent.includes("Chrome")) {
    browserStyle["paddingLeft"] = "10px";
  }

  if (navigator.userAgent.includes("Firefox")) {
    browserStyle["paddingLeft"] = "10px";
  }

  let lastBackgroundColor = "#1B76FF";

  const containerClasses = ["c-table-container"];
  if (props.scrollable) containerClasses.push("c-table-container--scrollable");
  if (secondPagination)
    containerClasses.push("c-table-container--second-pagination");

  let styleBorderRadius = {};

  styleBorderRadius["left"] = 10;
  styleBorderRadius["right"] = 10;

  if (props.disableBorderRadius) {
    styleBorderRadius["left"] = 0;
    styleBorderRadius["right"] = 0;
  }

  if (props.viewable && !props.disableBorderRadius) {
    styleBorderRadius["left"] = 10;
    styleBorderRadius["right"] = 0;
  }

  if (props.enableSelection && !props.disableBorderRadius) {
    styleBorderRadius["left"] = 0;
    styleBorderRadius["right"] = 10;
  }

  if (
    props.viewable &&
    !props.disableBorderRadius &&
    props.contentState !== STATE.IDLE
  ) {
    styleBorderRadius["right"] = props.contentState ? 10 : 0;
  }

  let borderBottomRadiusTable = {};
  if (secondPagination) {
    borderBottomRadiusTable = {
      borderBottomLeftRadius: "0px",
      borderBottomRightRadius: "0px",
    };
  }

  return (
    <div className={containerClasses.join(" ")}>
      <div
        className={
          secondPagination
            ? "c-table-container__content-border"
            : "c-table-container__content"
        }
      >
        <table className="c-table" style={{ ...borderBottomRadiusTable }}>
          {props.dataSource && props.dataSource.length > 0 && (
            <thead className={classNameThead} style={{ ...browserStyle }}>
              <tr>
                <th
                  style={
                    props.enableSelection &&
                    props.contentState !== STATE.LOADING
                      ? {
                          borderTopLeftRadius: "10px",
                        }
                      : { display: "none" }
                  }
                  onClick={() => {
                    handleSelectionChanged(!selectionHeader);
                  }}
                >
                  <input
                    style={
                      props.enableSelectionHeader ? {} : { display: "none" }
                    }
                    type="checkbox"
                    checked={selectionHeader}
                    onChange={() => {
                      // * handleSelectionChanged(!selected);
                    }}
                  />
                </th>
                {headers.map((h, i) => {
                  // * Check if width is provided
                  const style = {};
                  let colSpan = 1;
                  let rowSpan = 1;

                  // * Check nested columns and assign column span and row span
                  if (h.columns) {
                    colSpan = h.columns.length;
                  } else {
                    rowSpan = 2;
                  }

                  if (h.width) {
                    style.width = h.width;
                    style.overflow = "hidden";
                  }

                  if (h.minWidth) {
                    style.minWidth = h.minWidth;
                    style.overflow = "hidden";
                  }

                  if (h.maxWidth) {
                    style.maxWidth = h.maxWidth;
                    style.overflow = "hidden";
                  }

                  if (h.textAlign) {
                    style.textAlign = h.textAlign;
                  }

                  if (props.showBlock && i < headers.length - 1) {
                    style.borderRight = "1px solid #eaf0fa";
                  }

                  if (i === 0) {
                    style["borderTopLeftRadius"] = styleBorderRadius["left"];
                  }

                  if (i === headers.length - 1) {
                    style["borderTopRightRadius"] = styleBorderRadius["right"];
                  }

                  const sortedCol = [];

                  if (h.sortable) {
                    sortedCol.push(h.key);
                    style.cursor = "pointer";
                  }
                  const sortedLabelClassName = sortedCol.includes(h.key)
                    ? " c-table-header__label--sort"
                    : "";

                  return (
                    <th
                      key={`header-${i}`}
                      style={{
                        ...style,
                        backgroundColor: h.color || style.backgroundColor,
                        color: h.fontColor || style.color,
                        ...browserStyle,
                        borderBottom: `${
                          props.showBlock ? "1px solid #eaf0fa" : 0
                        }`,
                      }}
                      colSpan={colSpan}
                      rowSpan={rowSpan}
                      onClick={
                        sortedCol.includes(h.key)
                          ? () => handleOnSortedBy(h.key)
                          : null
                      }
                      className={classNameLabel + sortedLabelClassName}
                    >
                      <label style={{ position: "relative" }}>
                        {h.display}
                        <SortIconButton
                          icon={h.sortIcon}
                          sortNumber={sorted[h.key]}
                        />
                      </label>
                    </th>
                  );
                })}
                {props.viewable && props.contentState !== STATE.LOADING && (
                  <th />
                )}
              </tr>
              {indexOfGroupHeader.length !== 0 && (
                <tr>
                  <th
                    style={
                      props.enableSelection &&
                      props.contentState !== STATE.LOADING
                        ? {}
                        : { display: "none" }
                    }
                    onClick={() => {
                      handleSelectionChanged(!selectionHeader);
                    }}
                  >
                    <input
                      style={
                        props.enableSelectionHeader ? {} : { display: "none" }
                      }
                      type="checkbox"
                      checked={selectionHeader}
                      onChange={() => {
                        // * handleSelectionChanged(!selected);
                      }}
                    />
                  </th>
                  {headers.map((h, i) => {
                    if (Array.isArray(h.columns)) {
                      return h.columns.map((item, j) => {
                        const style = {
                          padding: "4px",
                          backgroundColor: `${
                            item.color ? item.color : "#1B76FF"
                          }`,
                        };
                        lastBackgroundColor = item.color;
                        if (item.width) {
                          style.width = item.width;
                          style.overflow = "hidden";
                        }

                        if (item.maxWidth) {
                          style.maxWidth = item.maxWidth;
                          style.overflow = "hidden";
                        }

                        if (item.fontColor) {
                          style.color = item.fontColor;
                        }

                        return (
                          <th
                            key={`group-header-${i}-${j}`}
                            style={{
                              ...style,
                              height: "42px",
                              borderRight:
                                props.showBlock &&
                                (i !== headers.length - 1 ||
                                  j !== h.columns.length - 1)
                                  ? "1px solid #eaf0fa"
                                  : 0,
                            }}
                          >
                            <label style={{ display: "block", ...style }}>
                              {item.display}
                            </label>
                          </th>
                        );
                      });
                    }
                  })}
                  {props.viewable && (
                    <th style={{ backgroundColor: lastBackgroundColor }} />
                  )}
                </tr>
              )}
            </thead>
          )}
          <tbody className="c-table-records">
            {getContentRender()}
            {/* Empty <tr /> below for make other <tr> in <tbody> not larger than default height of it if <tbody> height 100% when props.scrollable is true  */}
            <tr />
          </tbody>
        </table>
      </div>
      {secondPagination && <>{props.children}</>}
    </div>
  );
};

List.propTypes = {
  columns: PropTypes.array,
  scrollable: PropTypes.bool,
  dataSource: PropTypes.array,
  onRecordClick: PropTypes.func,
  viewable: PropTypes.bool,
  enableSelection: PropTypes.bool,
  enableSelectionHeader: PropTypes.bool,
  onAddSelection: PropTypes.func,
  onRemoveSelection: PropTypes.func,
  hasFooter: PropTypes.bool,
  onSort: PropTypes.func,
  moreIconTooltip: PropTypes.string,
  numberOfLines: PropTypes.number,
  verticalAlign: PropTypes.string,

  /**
   * Enable monospace font for numbers
   * @default false
   * @type {boolean}
   */
  monospaceNumbers: PropTypes.bool,

  /**
   * Disable border radius
   */
  disableBorderRadius: PropTypes.bool,

  /**
   * Show block of data
   */
  showBlock: PropTypes.bool,
};

List.defaultProps = {
  onSort: () => {},
  scrollable: false,
  monospaceNumbers: false,
};

export default List;
